import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { Observable, Subscription, forkJoin, of } from "rxjs";
import { BscArea, BscEjercicioKpi, BscNorma, BscRes } from "../../shared/model/bsc/bsc.model";
import { BscService } from "../bsc.service";

interface Candidato {
  id: number;
  objetivo: string;
  indicador: string;
  area: string;
  sugerido?: boolean;
}

@Component({
  selector: "app-indicadores-norma-bsc",
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatButtonModule, MatFormFieldModule, MatInputModule],
  templateUrl: "./indicadores-norma-bsc.component.html",
  styleUrls: ["../bsc.shared.css", "./indicadores-norma-bsc.component.css"],
})
export class IndicadoresNormaBscComponent implements OnChanges, OnDestroy {
  @Input() anio: number | null = null;
  @Input() soloLectura = true;
  @Input() areas: BscArea[] = [];
  @Output() guardandoCambio = new EventEmitter<boolean>();
  normas: BscNorma[] = [];
  idNorma?: number;
  candidatos: Candidato[] = [];
  filtro = "";
  loading = false;
  guardando = false;
  error = "";
  mensaje = "";
  editor: BscNorma | null = null;
  private readonly listas = new Map<number, number[]>();
  private consulta?: Subscription;
  private escritura?: Subscription;

  constructor(private readonly bsc: BscService, private readonly cdr: ChangeDetectorRef) {}

  ngOnChanges(): void { this.cargar(); }
  ngOnDestroy(): void {
    this.consulta?.unsubscribe();
    this.escritura?.unsubscribe();
  }

  get norma(): BscNorma | undefined { return this.normas.find((n) => n.idNorma === this.idNorma); }
  get bloqueada(): boolean { return this.soloLectura || this.loading || this.guardando; }
  get listaBloqueada(): boolean { return this.bloqueada || this.norma?.activa !== true || !!this.error; }
  get ids(): number[] { return this.listas.get(this.idNorma ?? 0) ?? []; }
  get elegidos(): Candidato[] {
    return this.ids.map((id) => this.candidatos.find((k) => k.id === id) ?? {
      id, objetivo: `KPI ${id}`, indicador: "", area: "Sin detalle disponible",
    });
  }
  get grupos(): { area: string; kpis: Candidato[] }[] {
    const filtro = normaliza(this.filtro);
    const grupos = new Map<string, Candidato[]>();
    for (const k of this.candidatos) {
      if (!normaliza(`${k.objetivo} ${k.indicador} ${k.area}`).includes(filtro)) { continue; }
      const lista = grupos.get(k.area) ?? [];
      lista.push(k);
      grupos.set(k.area, lista);
    }
    return Array.from(grupos, ([area, kpis]) => ({ area, kpis }));
  }

  cargar(): void {
    if (this.anio == null) { return; }
    const anio = this.anio;
    this.consulta?.unsubscribe();
    this.loading = true;
    this.error = "";
    this.editor = null;
    const consultas = this.areas.filter((a) => a.idBscArea != null)
      .map((a) => this.bsc.areaDetalle(anio, a.idBscArea as number, "4"));
    const areas = consultas.length > 0 ? forkJoin(consultas) : of([] as BscRes[]);
    this.consulta = forkJoin({ normas: this.bsc.indicadoresConsultar(anio, "4"), areas }).subscribe({
      next: (res) => {
        this.loading = false;
        const fallo = [res.normas, ...res.areas].find((r) => r.codigoError);
        if (fallo) {
          this.error = fallo.mensajeError ?? "No se pudieron consultar los KPI.";
        } else if (res.areas.some(lecturaIncompleta)) {
          this.error = "No se pudieron leer todas las áreas. Reintenta antes de editar la lista.";
        } else {
          this.aplicar(res.normas, res.areas);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.error = "No se pudieron consultar las normas y los KPI. Revisa tu conexión.";
        this.cdr.markForCheck();
      },
    });
  }

  private aplicar(res: BscRes, areas: BscRes[]): void {
    this.normas = res.normas ?? [];
    const indicadores = res.indicadores ?? [];
    const sugerencias = new Set(res.sugerencias ?? []);
    this.listas.clear();
    for (const n of this.normas) {
      if (n.idNorma == null) { continue; }
      this.listas.set(n.idNorma, indicadores.filter((i) => i.idNorma === n.idNorma)
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
        .map((i) => i.idKpi).filter((id): id is number => id != null));
    }
    if (!this.norma) { this.idNorma = this.normas[0]?.idNorma; }
    this.candidatos = areas.flatMap((r) => (r.areaDetalle?.kpis ?? [])
      .filter((k) => k.idKpi != null)
      .map((k) => candidato(k, r.areaDetalle?.area?.nombre ?? "Sin área", sugerencias)));
  }

  elegir(id?: number): void { this.idNorma = id; this.editor = null; this.mensaje = ""; }
  alternar(id: number): void {
    if (this.listaBloqueada || this.idNorma == null) { return; }
    const ids = this.ids;
    this.listas.set(this.idNorma, ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]);
    this.mensaje = "Cambios sin guardar en esta norma.";
  }
  mover(indice: number, desplazamiento: number): void {
    const destino = indice + desplazamiento;
    if (this.listaBloqueada || this.idNorma == null || destino < 0 || destino >= this.ids.length) { return; }
    const ids = [...this.ids];
    [ids[indice], ids[destino]] = [ids[destino], ids[indice]];
    this.listas.set(this.idNorma, ids);
    this.mensaje = "Cambios sin guardar en esta norma.";
  }
  agregarNorma(): void { this.editor = { nombre: "", activa: true, orden: this.normas.length + 1 }; }
  editarNorma(): void { if (this.norma) { this.editor = { ...this.norma }; } }
  guardarNorma(): void {
    const nombre = this.editor?.nombre?.trim() ?? "";
    if (this.bloqueada || !this.editor || !nombre || nombre.length > 60) { return; }
    const norma = { ...this.editor, nombre };
    this.escribir(this.bsc.indicadoresNorma(norma), (res) => {
      const idNorma = res.idGenerado ?? norma.idNorma;
      const indice = this.normas.findIndex((n) => n.idNorma === idNorma);
      const guardada = { ...norma, idNorma };
      if (indice < 0) { this.normas = [...this.normas, guardada]; }
      else { this.normas = this.normas.map((n, i) => i === indice ? guardada : n); }
      this.idNorma = idNorma;
      this.editor = null;
      this.mensaje = "Norma guardada.";
    });
  }
  cambiarEstado(): void {
    if (this.bloqueada || !this.norma) { return; }
    this.editor = { ...this.norma, activa: !this.norma.activa };
    this.guardarNorma();
  }
  guardarLista(): void {
    if (this.listaBloqueada || this.anio == null || this.idNorma == null) { return; }
    const id = this.idNorma;
    const ids = [...this.ids];
    this.escribir(this.bsc.indicadoresLista(this.anio, id, ids), () => {
      this.normas = this.normas.map((n) => n.idNorma === id ? { ...n, total: ids.length } : n);
      this.mensaje = "Lista guardada.";
    });
  }
  private escribir(pedido: Observable<BscRes>, exito: (res: BscRes) => void): void {
    this.guardando = true;
    this.guardandoCambio.emit(true);
    this.mensaje = "";
    this.escritura = pedido.subscribe({
      next: (res) => {
        this.guardando = false;
        this.guardandoCambio.emit(false);
        if (res.codigoError) { this.mensaje = res.mensajeError ?? "No se pudo guardar."; }
        else { exito(res); }
        this.cdr.markForCheck();
      },
      error: () => {
        this.guardando = false;
        this.guardandoCambio.emit(false);
        this.mensaje = "No se pudo confirmar el guardado. Revisa tu conexión y vuelve a consultar.";
        this.cdr.markForCheck();
      },
    });
  }
}

function normaliza(texto: string): string {
  return texto.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}
function lecturaIncompleta(r: BscRes): boolean {
  const d = r.areaDetalle;
  return !d || d.cicloLeido === false || d.planeacionesLeidas === false || d.kpisLeidos === false;
}
function candidato(k: BscEjercicioKpi, area: string, sugerencias: ReadonlySet<number>): Candidato {
  return { id: k.idKpi as number, objetivo: k.objetivo ?? "Sin objetivo", indicador: k.indicador ?? "", area,
    sugerido: sugerencias.has(k.idEjercicioKpi) };
}
